// Public reporting: distinct routes, not one generic support button.
//
// A wrong address, an unsafe vehicle, a consumer complaint and a privacy
// request are four different things with four different registers and four
// different deadlines. Putting them behind one "Contact support" link loses
// all four, which is exactly what this page exists to avoid.
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Building2, Gavel, Lock, Search, ShieldAlert, Store } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { fmtDate, humanise, legalError } from "@/lib/platformLegal";
import CachePublicShell from "@/components/CachePublicShell";
import SEO from "@/components/SEO";

const KINDS = [
  { key: "business", label: "Business listing or verification", icon: Building2,
    blurb: "Claim a business, report wrong information, question a verification label, or ask "
      + "for a correction or removal." },
  { key: "safety", label: "Safety or legal concern", icon: AlertTriangle,
    blurb: "An unsafe vehicle, a dangerous or unlicensed driver, a business or vehicle operating "
      + "without what it needs, or a fraudulent document." },
  { key: "complaint", label: "Booking, money or dispute", icon: Gavel,
    blurb: "A booking or payment problem, an advance, security deposit or refund issue, or a "
      + "damage, insurance or rental dispute." },
  { key: "privacy", label: "Privacy request", icon: Lock,
    blurb: "Access, correct, delete or object to the use of your personal data." },
  { key: "my-business", label: "I run this business", icon: Store,
    blurb: "Challenge or correct what ConfirmPH shows about your own business, vehicles or "
      + "drivers." },
];

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm " +
  "focus:border-slate-900 focus:outline-none";

export default function ReportCentre() {
  const [params, setParams] = useSearchParams();
  const kind = KINDS.find((k) => k.key === params.get("kind"))?.key || null;
  const companyId = params.get("company") || "";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="report-centre">
      <CachePublicShell />
      <SEO title="Report a problem — ConfirmPH"
           description="Report a business listing problem, a safety issue, a consumer complaint
                        or a privacy request to ConfirmPH." />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Report a problem</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">
        Pick what this is about. Each kind of report goes to a different team with its own
        response commitment, so choosing the right one gets it looked at faster.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="report-kinds">
        {KINDS.map((item) => {
          const Icon = item.icon;
          const active = kind === item.key;
          return (
            <button key={item.key}
                    onClick={() => setParams(
                      companyId ? { kind: item.key, company: companyId } : { kind: item.key })}
                    data-testid={`report-kind-${item.key}`}
                    className={`rounded-xl border p-4 text-left ${active
                      ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-900">{item.label}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{item.blurb}</p>
            </button>
          );
        })}
      </div>

      {kind === "business" && <BusinessForm companyId={companyId} />}
      {kind === "my-business" && <BusinessForm companyId={companyId} representative />}
      {kind === "safety" && <SafetyForm companyId={companyId} />}
      {kind === "complaint" && <ComplaintForm companyId={companyId} />}
      {kind === "privacy" && <PrivacyForm />}

      <Lookup />

      <Link to="/security" data-testid="report-kind-security"
            className="mt-3 flex items-start gap-2 rounded-xl border border-slate-200 p-4
                       hover:bg-slate-50">
        <ShieldAlert className="w-4 h-4 mt-0.5 text-slate-600" />
        <span>
          <span className="font-medium text-slate-900">Security vulnerability or incident</span>
          <span className="block text-sm text-slate-600">
            A weakness in ConfirmPH itself, or data you think was exposed. This goes to the
            security route, not to support.
          </span>
        </span>
      </Link>

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-2">
        <Link to="/what-we-verify" className="underline block">What ConfirmPH verifies</Link>
        <Link to="/" className="underline block">← Back to home</Link>
      </div>
    </div>
  );
}

function Submitted({ payload, note }) {
  return (
    <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
         data-testid="report-submitted">
      <p className="text-sm font-medium text-emerald-900">
        Received. Your reference is {payload.reference}.
      </p>
      <p className="mt-1 text-sm text-emerald-900">{note}</p>
      {payload.due_at && (
        <p className="mt-1 text-xs text-emerald-800">
          We aim to respond by {fmtDate(payload.due_at)}.
        </p>
      )}
    </div>
  );
}

function useSubmit(endpoint, note) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/legal/${endpoint}`, body);
      setResult(data);
      toast.success("Report received.");
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };
  const view = result ? <Submitted payload={result} note={note} /> : null;
  return { submit, busy, result, view };
}

function Form({ children, onSubmit, busy, submitLabel, testId, footnote }) {
  return (
    <form className="mt-6 space-y-3" data-testid={testId}
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {children}
      {footnote && <p className="text-xs text-slate-500">{footnote}</p>}
      <button type="submit" disabled={busy}
              className="w-full sm:w-auto rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium
                         text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid={`${testId}-submit`}>
        {busy ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}

function Labelled({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}

function BusinessForm({ companyId, representative = false }) {
  const [form, setForm] = useState({
    request_type: representative ? "business_challenge" : "incorrect_information",
    company_id: companyId, requester_name: "", requester_email: "", reason: "",
    requester_role: "",
  });
  const { submit, busy, result, view } = useSubmit(
    "reports/business",
    "A person reviews every request. ConfirmPH does not transfer control of a business profile, "
    + "change public information or remove a listing automatically.");
  if (result) return view;

  return (
    <Form testId={representative ? "report-my-business-form" : "report-business-form"}
          busy={busy} submitLabel="Send request"
          onSubmit={() => submit(form)}
          footnote={representative
            ? "Tell us which label, status or piece of information you are challenging and why. "
              + "A person reviews it against the evidence; nothing changes automatically."
            : "Knowing a business's name and email is not proof of ownership, so a claim is "
              + "reviewed against evidence before anything changes."}>
      {representative ? (
        <Labelled label="Your role at the business" hint="For example owner or manager.">
          <input className={input} value={form.requester_role}
                 onChange={(e) => setForm({ ...form, requester_role: e.target.value })}
                 data-testid="report-business-role" />
        </Labelled>
      ) : (
        <Labelled label="What do you need?">
          <select className={input} value={form.request_type}
                  onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                  data-testid="report-business-type">
            <option value="claim">Claim this business</option>
            <option value="incorrect_information">Report incorrect information</option>
            <option value="verification_concern">Question a verification or readiness label</option>
            <option value="correction">Request a correction</option>
            <option value="removal">Request removal</option>
          </select>
        </Labelled>
      )}
      <Labelled label="Business ID" hint="From the address bar of the business's ConfirmPH page.">
        <input className={input} value={form.company_id}
               onChange={(e) => setForm({ ...form, company_id: e.target.value })}
               data-testid="report-business-company" />
      </Labelled>
      <Labelled label="Your name">
        <input className={input} value={form.requester_name}
               onChange={(e) => setForm({ ...form, requester_name: e.target.value })} />
      </Labelled>
      <Labelled label="Your email">
        <input type="email" className={input} value={form.requester_email}
               onChange={(e) => setForm({ ...form, requester_email: e.target.value })} />
      </Labelled>
      <Labelled label="What is wrong, or what do you need?">
        <textarea className={input} rows={4} value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </Labelled>
    </Form>
  );
}

function SafetyForm({ companyId }) {
  const [form, setForm] = useState({
    allegation: "unsafe_tyres", severity: "high", company_id: companyId,
    vehicle_id: "", reporter_name: "", reporter_email: "", description: "",
  });
  const { submit, busy, result, view } = useSubmit(
    "reports/safety",
    "Safety reports are triaged first. A report is an allegation until it is assessed, and any "
    + "restriction ConfirmPH applies is temporary and reviewable. ConfirmPH is not a government "
    + "authority; where only an authority can decide, it may refer the matter.");
  if (result) return view;

  return (
    <Form testId="report-safety-form" busy={busy} submitLabel="Send safety report"
          onSubmit={() => submit(form)}
          footnote="You do not need an account, and you do not have to give an email address —
                    though we cannot follow up without one.">
      <Labelled label="What is the problem?">
        <select className={input} value={form.allegation}
                onChange={(e) => setForm({ ...form, allegation: e.target.value })}
                data-testid="report-safety-allegation">
          <option value="brake_or_steering_failure">Brake or steering failure</option>
          <option value="unsafe_tyres">Unsafe tyres</option>
          <option value="vehicle_relisted_after_serious_accident">
            Relisted after a serious accident
          </option>
          <option value="fraudulent_safety_document">A fraudulent safety document</option>
          <option value="dangerous_driver">A dangerous driver</option>
          <option value="vehicle_reported_stolen_or_impounded">Stolen or impounded vehicle</option>
          <option value="no_valid_registration">No valid registration</option>
          <option value="unsafe_modification">An unsafe modification</option>
          <option value="business_operating_without_required_authority">
            A business operating without a required permit or authority
          </option>
          <option value="vehicle_insurance_concern">A vehicle's insurance looks invalid</option>
          <option value="driver_not_licensed_or_not_authorized">
            A driver who is unlicensed or not authorized
          </option>
          <option value="other_immediate_safety_risk">Another immediate safety risk</option>
        </select>
      </Labelled>
      <Labelled label="How urgent is it?">
        <select className={input} value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}>
          <option value="critical">Someone could be hurt right now</option>
          <option value="high">Serious, needs looking at quickly</option>
          <option value="standard">Should be checked</option>
        </select>
      </Labelled>
      <Labelled label="Business ID"><input className={input} value={form.company_id}
             onChange={(e) => setForm({ ...form, company_id: e.target.value })} /></Labelled>
      <Labelled label="Vehicle ID (optional)"><input className={input} value={form.vehicle_id}
             onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} /></Labelled>
      <Labelled label="Your name"><input className={input} value={form.reporter_name}
             onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} /></Labelled>
      <Labelled label="Your email (optional)">
        <input type="email" className={input} value={form.reporter_email}
               onChange={(e) => setForm({ ...form, reporter_email: e.target.value })} />
      </Labelled>
      <Labelled label="What did you see?">
        <textarea className={input} rows={4} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="report-safety-description" />
      </Labelled>
    </Form>
  );
}

function ComplaintForm({ companyId }) {
  const [form, setForm] = useState({
    category: "booking_or_payment_issue", company_id: companyId, booking_id: "",
    complainant_name: "", complainant_email: "", description: "", requested_remedy: "",
  });
  const { submit, busy, result, view } = useSubmit(
    "reports/complaint",
    "ConfirmPH records and mediates complaints. It may preserve evidence, help you and the "
    + "business communicate, and apply its own platform policies, but it does not decide civil "
    + "liability, and it does not hold or refund rental payments or security deposits — those "
    + "are between you and the rental business.");
  if (result) return view;

  return (
    <Form testId="report-complaint-form" busy={busy} submitLabel="Send complaint"
          onSubmit={() => submit(form)}
          footnote="ConfirmPH aims to respond within seven calendar days.">
      <Labelled label="What is the complaint about?">
        <select className={input} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="report-complaint-category">
          <option value="booking_or_payment_issue">A booking or payment problem</option>
          <option value="advance_or_security_deposit_issue">
            An advance, security deposit or refund issue
          </option>
          <option value="damage_or_insurance_claim">A damage or insurance claim</option>
          <option value="rental_dispute_escalation">
            A rental dispute I could not settle with the business
          </option>
          <option value="booking_not_honoured">The booking was not honoured</option>
          <option value="vehicle_condition">The vehicle's condition</option>
          <option value="deposit_not_returned">A security deposit was not returned</option>
          <option value="overcharging_or_hidden_fees">Overcharging or hidden fees</option>
          <option value="misleading_listing">A misleading listing</option>
          <option value="cancellation_or_refund">Cancellation or refund</option>
          <option value="safety_concern">A safety concern</option>
          <option value="staff_conduct">Staff conduct</option>
          <option value="discrimination">Discrimination</option>
          <option value="privacy_concern">A privacy concern</option>
          <option value="platform_error">A problem with ConfirmPH itself</option>
          <option value="other">Something else</option>
        </select>
      </Labelled>
      <Labelled label="Business ID"><input className={input} value={form.company_id}
             onChange={(e) => setForm({ ...form, company_id: e.target.value })} /></Labelled>
      <Labelled label="Booking reference (optional)">
        <input className={input} value={form.booking_id}
               onChange={(e) => setForm({ ...form, booking_id: e.target.value })} />
      </Labelled>
      <Labelled label="Your name"><input className={input} value={form.complainant_name}
             onChange={(e) => setForm({ ...form, complainant_name: e.target.value })} /></Labelled>
      <Labelled label="Your email"><input type="email" className={input}
             value={form.complainant_email}
             onChange={(e) => setForm({ ...form, complainant_email: e.target.value })} /></Labelled>
      <Labelled label="What happened?">
        <textarea className={input} rows={4} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="report-complaint-description" />
      </Labelled>
      <Labelled label="What would resolve it?">
        <textarea className={input} rows={2} value={form.requested_remedy}
                  onChange={(e) => setForm({ ...form, requested_remedy: e.target.value })} />
      </Labelled>
    </Form>
  );
}

function PrivacyForm() {
  const [form, setForm] = useState({
    request_type: "access", requester_email: "", requester_name: "", description: "",
  });
  const { submit, busy, result, view } = useSubmit(
    "privacy-requests",
    "We will confirm who you are before anything is shared or deleted. Where a record has to be "
    + "kept by law, we will tell you which one and why.");
  if (result) return view;

  return (
    <Form testId="report-privacy-form" busy={busy} submitLabel="Send privacy request"
          onSubmit={() => submit(form)}
          footnote="ConfirmPH aims to give a substantive answer within 15 days.">
      <Labelled label="What are you asking for?">
        <select className={input} value={form.request_type}
                onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                data-testid="report-privacy-type">
          <option value="access">Access my data</option>
          <option value="export">Export my data</option>
          <option value="correction">Correct my data</option>
          <option value="deletion">Delete my data</option>
          <option value="objection">Object to processing</option>
          <option value="restriction">Restrict or block processing</option>
          <option value="portability">Data portability</option>
          <option value="consent_withdrawal">Withdraw consent</option>
          <option value="privacy_complaint">Make a privacy complaint</option>
        </select>
      </Labelled>
      <Labelled label="Your name"><input className={input} value={form.requester_name}
             onChange={(e) => setForm({ ...form, requester_name: e.target.value })} /></Labelled>
      <Labelled label="Your email"
                hint="Use the address on your ConfirmPH account if you have one.">
        <input type="email" className={input} value={form.requester_email}
               onChange={(e) => setForm({ ...form, requester_email: e.target.value })} />
      </Labelled>
      <Labelled label="Tell us what you need">
        <textarea className={input} rows={4} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="report-privacy-description" />
      </Labelled>
    </Form>
  );
}

function Lookup() {
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const look = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/legal/requests/lookup", { params: { email } });
      setRows(data);
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  const all = [...(rows?.complaints || []), ...(rows?.safety_reports || []),
               ...(rows?.business_requests || []), ...(rows?.privacy_requests || [])];
  return (
    <section className="mt-10 rounded-xl border border-slate-200 p-4" data-testid="report-lookup">
      <h2 className="font-display text-lg font-semibold">Check something you already sent</h2>
      <p className="mt-1 text-sm text-slate-600">
        Enter the email you used to see the reference and current status of each report or
        request. You will only ever see your own.
      </p>
      <form className="mt-3 flex flex-wrap gap-2"
            onSubmit={(e) => { e.preventDefault(); look(); }}>
        <input type="email" className={`${input} flex-1 min-w-[12rem]`} value={email}
               onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
               data-testid="report-lookup-email" />
        <button type="submit" disabled={busy || !email}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium
                           text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                data-testid="report-lookup-submit">
          <Search className="w-4 h-4 inline" /> Look up
        </button>
      </form>
      {rows && (
        all.length === 0 ? (
          <p className="mt-3 text-sm text-slate-600" data-testid="report-lookup-empty">
            Nothing was found for that address.
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="report-lookup-results">
            {all.map((row) => (
              <li key={row.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs">{row.reference}</span>
                  <span className="text-xs text-slate-600">{humanise(row.status)}</span>
                </div>
                <div className="mt-0.5 text-xs text-slate-600">
                  {row.label || humanise(row.category)} · sent {fmtDate(row.received_at)}
                </div>
                {row.response && <p className="mt-1 text-sm text-slate-800">{row.response}</p>}
                {row.outcome_note && (
                  <p className="mt-1 text-sm text-slate-800">{row.outcome_note}</p>
                )}
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  );
}
